import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./FoldPaywall.module.scss"

/**
 * FoldPaywall (`/fold`) — an original, self-contained ONBOARDING paywall for
 * Private Photo Vault Premium. Paywall type rolled at random for this run:
 * ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — but from a fresh
 * angle: privacy as CONSOLIDATION, not a stronger lock.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Bring them into the fold." The honest, first-run truth a photo vault exists
 * to fix: right now your most personal photos aren't in one private place — the
 * same shots live in your Camera Roll, in a shared album, in an iCloud backup,
 * on your lock-screen widget, and inside every app you've handed photo access.
 * Private Photo Vault gives them ONE home that is only yours. This screen shows
 * that exposure honestly, then gathers everything home in a single gesture.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * The vault sits at the centre; your memories float OUT IN THE OPEN around it,
 * each pinned to the everyday surface that can currently see it. Tap "Bring them
 * home" (or the vault) and every photo is drawn magnetically inward — a radial
 * IN-GATHER — while its exposure tag extinguishes one by one and flips to "In
 * your vault". The vault folds shut and a calm seal reads "Seen by only you."
 * Tap again to scatter them back out ("show me the risk again"). Not a lock you
 * turn, a door you shut, a key you drag, or a hold you press — a GATHER.
 *
 * ── Divergence (vs. the 130+ prior concepts, incl. the security cluster) ────
 * Layout      : an EXPOSURE FIELD — memories orbiting a central vault, each on
 *               its own "who can see this" chip — not a wall, door, folio, grid,
 *               keypad, dial, radar, tabletop device or frosted window.
 * Interaction : TAP-TO-GATHER radial magnetic in-gather (replayable). Not a
 *               hold (Nightlatch/Ward), a drag-key (Cipher/Static), a flip
 *               (Flipside), a zip (Seam/Carryall) or a tap-authenticate stamp
 *               (Eyes Only).
 * Metaphor    : SCATTERED-IN-THE-OPEN → GATHERED-HOME. Consolidation, not a
 *               bigger lock.
 * Value frame : CONTRAST / honest privacy math — "seen by 5 places → seen by
 *               only you." Not loss, urgency or scarcity.
 * Palette     : LIGHT & airy daylight onboarding — a deliberate break from the
 *               recent dark cluster (Nightlatch, Safelight, Flipside, Tumbler).
 * Register    : warm ownership / quiet relief — "everything that's mine, home."
 * CTA         : "Bring them into the fold" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every exposure surface named is real (photos on a phone genuinely live across
 * the camera roll, shared albums, cloud backups, widgets and photo-permissioned
 * apps) and every safeguard named is a REAL Private Photo Vault feature (only-you
 * lock with Face ID / Touch ID / a code · encrypted Cloud Vault · break-in
 * snapshots · app disguise). No countdown, no fabricated scarcity, NO data-loss
 * threat — nothing is deleted from anywhere; the vault simply gives your photos a
 * private home. "Save 79%" is exact arithmetic from live product data. Yearly is
 * the featured, default, most prominent price; monthly is small and secondary.
 * The 7-day free trial is the real trial. Renders on built-in debug product data
 * with bundled photos — no native injection, no external egress.
 */

// Your memories — bundled, self-contained repo photos. Each floats out in the
// open, pinned to a real everyday surface that can currently see it, until you
// bring it home. Angles/radii are hand-placed so the exposure field reads as a
// personal album scattered around one vault.
const MEMORIES = [
  { src: "/byheart/m01.jpg", where: "Camera Roll", x: -112, y: -96, r: -9 },
  { src: "/byheart/m05.jpg", where: "Shared album", x: 112, y: -100, r: 8 },
  { src: "/byheart/m11.jpg", where: "iCloud backup", x: 130, y: 26, r: 10 },
  {
    src: "/byheart/m03.jpg",
    where: "Lock-screen widget",
    x: -130,
    y: 28,
    r: -11,
  },
  { src: "/byheart/m14.jpg", where: "Apps you've let in", x: 0, y: 122, r: 3 },
]

// What "only yours" actually means — the real safeguards that make the vault a
// private home. Kept secondary to the hero gather above.
const MEANS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "A lock only you know",
    line: "Face ID, Touch ID or a private code — nobody else gets in",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private, encrypted backup — yours across every device",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "A silent photo of anyone who tries the wrong code",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the whole vault behind an ordinary-looking app",
  },
]

const FoldPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Blend the safe-area insets into the airy scene so it runs edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#eef2fb")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── gather state ────────────────────────────────────────────────────────
  // g: 0 = scattered / out in the open (the honest problem), 1 = gathered home
  // into the vault (only yours). A single spring chases the target; tapping
  // toggles it. `home` latches once nearly gathered so copy/seal can resolve.
  const [g, setG] = useState(0)
  const gRef = useRef(0)
  const targetRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)

  const home = g > 0.82

  const tick = useCallback(() => {
    const target = targetRef.current
    const cur = gRef.current
    // Gathering in (toward 1) has a touch more pull than scattering back out.
    const k = target > cur ? 0.14 : 0.11
    let next = cur + (target - cur) * k
    if (Math.abs(target - next) < 0.0012) next = target
    gRef.current = next
    setG(next)
    if (next !== target) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [])

  const setTarget = useCallback(
    (v: number) => {
      targetRef.current = v
      if (reduceMotion) {
        if (raf.current != null) cancelAnimationFrame(raf.current)
        raf.current = null
        gRef.current = v
        setG(v)
        return
      }
      if (raf.current == null) raf.current = requestAnimationFrame(tick)
    },
    [reduceMotion, tick],
  )

  const toggle = useCallback(() => {
    userActed.current = true
    setTarget(targetRef.current > 0.5 ? 0 : 1)
  }, [setTarget])

  // Auto-demo: hold the exposed "problem" state for a beat so it reads, then
  // gather everything home so the payoff lands in one motion. Reduced-motion
  // arrives already gathered (the calm end state).
  useEffect(() => {
    if (reduceMotion) {
      targetRef.current = 1
      gRef.current = 1
      setG(1)
      return
    }
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) setTarget(1)
    }, 1050)
    return () => {
      window.clearTimeout(t)
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // How many surfaces can still see your photos right now (drops to 0 as you
  // gather them home) — the honest privacy math, shown live.
  const exposedCount = Math.round(MEMORIES.length * (1 - g))

  return (
    <div
      className={`${styles.root} ${home ? styles.isHome : ""}`}
      style={{ ["--g" as any]: g }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Welcome to Private Photo Vault
          </span>
          <h1 className={styles.title}>
            {home ? (
              <>
                Now they're
                <br />
                <span className={styles.accent}>only yours.</span>
              </>
            ) : (
              <>
                Your memories are
                <br />
                <span className={styles.accentWarn}>out in the open.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            {home
              ? "Every photo, gathered into one private home. No shared albums, no stray backups, no app peeking in — seen by only you."
              : "The same photos live in your camera roll, a shared album, a cloud backup and every app you've let in. Let's bring them somewhere that's only yours."}
          </p>
        </section>

        {/* ── the exposure field (hero) ──────────────────────────────────── */}
        <section className={styles.stage}>
          {/* live privacy math */}
          <div
            className={`${styles.meter} ${home ? styles.meterHome : ""}`}
            aria-live="polite"
          >
            <span className={styles.meterDot} aria-hidden="true" />
            {home ? (
              <>
                Seen by <b>only you</b>
              </>
            ) : (
              <>
                Seen by{" "}
                <b>
                  {exposedCount} other place{exposedCount === 1 ? "" : "s"}
                </b>
              </>
            )}
          </div>

          <button
            type="button"
            className={styles.field}
            onClick={toggle}
            aria-label={
              home
                ? "Scatter your photos back out to see the risk again"
                : "Bring your photos home into the vault"
            }
          >
            {/* faint pull glow from the open to the vault */}
            <span className={styles.pull} aria-hidden="true" />
            {/* calm concentric halo that blooms as everything gathers home */}
            <span className={styles.halo} aria-hidden="true" />
            <span
              className={`${styles.halo} ${styles.halo2}`}
              aria-hidden="true"
            />

            {/* the memories, floating out in the open. Each gets its own eased
                progress so they stream home in a staggered magnetic in-gather
                rather than all snapping at once. */}
            {MEMORIES.map((m, i) => {
              const spread = 0.34
              const p = Math.max(
                0,
                Math.min(
                  1,
                  (g - (i * spread) / MEMORIES.length) / (1 - spread),
                ),
              )
              return (
                <span
                  key={m.src}
                  className={styles.mem}
                  style={{
                    backgroundImage: `url(${m.src})`,
                    ["--x" as any]: `${m.x}px`,
                    ["--y" as any]: `${m.y}px`,
                    ["--r" as any]: `${m.r}deg`,
                    ["--p" as any]: p,
                    ["--i" as any]: i,
                  }}
                >
                  <span className={styles.chip} aria-hidden="true">
                    <span className={styles.chipEye}>
                      <svg viewBox="0 0 24 24" width="9" height="9">
                        <path
                          d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                        />
                        <circle cx="12" cy="12" r="2.4" fill="currentColor" />
                      </svg>
                    </span>
                    {m.where}
                  </span>
                </span>
              )
            })}

            {/* the vault at the centre — folds shut around your memories */}
            <span className={styles.vault} aria-hidden="true">
              <span className={styles.vaultBody}>
                <span className={styles.vaultStack} />
                <span className={styles.vaultLidL} />
                <span className={styles.vaultLidR} />
                <span className={styles.vaultSeal}>
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path
                      d="M6 11.5V8.5a6 6 0 0 1 12 0v3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <rect
                      x="4.5"
                      y="11"
                      width="15"
                      height="9.5"
                      rx="2.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M9.2 15.6l2.1 2.1 3.6-3.9"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </span>
              <span className={styles.vaultLabels}>
                <span className={styles.vaultLabel}>Your vault</span>
                <span className={styles.vaultSub}>
                  {MEMORIES.length} memories · only you
                </span>
              </span>
            </span>
          </button>

          {/* the one gesture */}
          <button
            type="button"
            className={styles.gather}
            onClick={toggle}
            aria-pressed={home}
          >
            <span className={styles.gatherIcon} aria-hidden="true">
              {home ? (
                <svg viewBox="0 0 24 24" width="17" height="17">
                  <path
                    d="M12 21c-4.5-1.6-7.5-5.5-7.5-10V6.2L12 3.4l7.5 2.8V11c0 4.5-3 8.4-7.5 10z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 12l2.2 2.2L15.3 10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="17" height="17">
                  <path
                    d="M4 8l4-4M4 8l4 4M4 8h6M20 16l-4 4M20 16l-4-4M20 16h-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="12" r="2.3" fill="currentColor" />
                </svg>
              )}
            </span>
            <span className={styles.gatherLabel}>
              {home ? "Show me the risk again" : "Bring them home"}
            </span>
          </button>
        </section>

        {/* ── honest framing of the concept ──────────────────────────────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Nothing gets deleted from anywhere — your photos simply gain a private
          home that only you can open.
        </p>

        {/* ── what "only yours" means (real safeguards, secondary) ────────── */}
        <ul className={styles.means}>
          <li className={styles.meansHead}>
            <span>What “only yours” means</span>
            <span className={styles.meansCount}>{MEANS.length} safeguards</span>
          </li>
          {MEANS.map((m) => (
            <li key={m.key} className={styles.meansRow}>
              <span className={styles.meansIcon} aria-hidden="true">
                <m.Icon width={18} height={18} />
              </span>
              <span className={styles.meansText}>
                <span className={styles.meansName}>{m.name}</span>
                <span className={styles.meansLine}>{m.line}</span>
              </span>
              <span className={styles.meansTick} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </li>
          ))}
        </ul>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Premium · 1 year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyFoot}>
              Every safeguard, on for a full year
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or monthly</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Bring them into the fold — ${trialDays}-day free trial`
              : "Bring them into the fold"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default FoldPaywall
